/**
 * Small input components matching the editorial design system.
 */
import { Sparkles } from "lucide-react";

export function FieldLabel({ children, hint }) {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <label className="text-xs uppercase tracking-[0.2em] text-[#5C5B5A] font-medium">
        {children}
      </label>
      {hint && <span className="text-[10px] text-[#8A8886]">{hint}</span>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, type = "text", testId, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-2.5 outline-none transition-all"
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, placeholder, rows = 4, testId }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testId}
      className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-3 outline-none transition-all resize-y leading-relaxed"
    />
  );
}

export function SectionCard({ title, subtitle, children, action, testId }) {
  return (
    <section
      className="bg-white border border-[#E8E6E1] rounded-sm p-8"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2
            className="text-2xl sm:text-3xl tracking-tight text-[#1A1A1A]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
          >
            {title}
          </h2>
          {subtitle && <p className="mt-2 text-sm text-[#5C5B5A]">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div>{children}</div>
    </section>
  );
}

export function PrimaryButton({ children, onClick, testId, type = "button", disabled, className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{ backgroundImage: "linear-gradient(120deg, #2563EB, #7C3AED)" }}
      className={`inline-flex items-center justify-center gap-2 text-white rounded-sm px-6 py-2.5 text-sm shadow-sm transition-all hover:shadow-md hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, testId, type = "button", disabled, className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-2 border border-[#E8E6E1] bg-white text-[#1A1A1A] hover:border-[#2563EB] rounded-sm px-5 py-2 text-sm transition-all disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

export function AIBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#B89154] border border-[#B89154]/30 bg-[#F5EFE6] rounded-sm px-2 py-0.5">
      <Sparkles className="h-3 w-3" strokeWidth={1.5} />
      AI
    </span>
  );
}

export function Overline({ children }) {
  return (
    <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A8886]">
      {children}
    </div>
  );
}

/**
 * Reusable in-page tab bar used inside modules to split long content into
 * focused workspaces. Tabs are visually inline (border-bottom indicator) and
 * include a numbered prefix + optional icon for editorial feel.
 *
 * `tabs`: [{ key, label, icon?, hint? }]
 */
export function SubTabs({ tabs, active, onChange, testIdPrefix = "subtab" }) {
  return (
    <div className="flex flex-wrap border-b border-[#E8E6E1] mb-6" data-testid={`${testIdPrefix}-bar`}>
      {tabs.map((t, idx) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative flex items-center gap-2 px-5 py-3 text-sm transition-all border-b-2 ${
              isActive
                ? "border-[#2563EB] text-[#2563EB] font-medium"
                : "border-transparent text-[#5C5B5A] hover:text-[#1A1A1A]"
            }`}
            data-testid={`${testIdPrefix}-${t.key}`}
          >
            <span
              className="font-mono text-[10px] text-[#8A8886]"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {String(idx + 1).padStart(2, "0")}
            </span>
            {Icon && <Icon className="h-4 w-4" strokeWidth={1.5} />}
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}
